import { Suspense, lazy, type ReactNode } from 'react';
import { Link, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Shell } from '@/layout/Shell';
import { useAuth } from '@/lib/auth';
import { Spinner } from '@/components/ui/primitives';
import { SignIn } from '@/pages/SignIn';
import { Dashboard } from '@/pages/Dashboard';

// Route-level code splitting: the warehouse tablet should not download the
// reporting and charting bundles it will never open.
const Products = lazy(() => import('@/pages/products'));
const Procurement = lazy(() => import('@/pages/procurement'));
const Warehouse = lazy(() => import('@/pages/warehouse'));
const Sales = lazy(() => import('@/pages/sales'));
const Customers = lazy(() => import('@/pages/customers'));
const Trials = lazy(() => import('@/pages/trials'));
const Machines = lazy(() => import('@/pages/machines'));
const Service = lazy(() => import('@/pages/service'));
const Documents = lazy(() => import('@/pages/documents'));
const Reports = lazy(() => import('@/pages/reports'));
const Settings = lazy(() => import('@/pages/settings'));
const TasksPage = lazy(() => import('@/pages/tasks'));

function Loading() {
  return (
    <div className="flex items-center justify-center py-20">
      <Spinner />
    </div>
  );
}


/**
 * Permission required to open each module, mirroring the server's
 * `visibleModules`.
 *
 * The navigation already hides what a role cannot use, but a typed URL, an old
 * bookmark or a link pasted into a chat all bypass the navigation. Without this
 * the page mounts, every query 403s, and the user is left staring at skeleton
 * rows that never resolve — the worst of both worlds, since it looks like a
 * broken page rather than a closed door.
 */
const MODULE_PERMISSIONS: Array<[string, string]> = [
  ['products', 'products.view'],
  ['procurement', 'procurement.view'],
  ['warehouse', 'warehouse.view'],
  ['sales', 'sales.view'],
  ['customers', 'customers.view'],
  ['trials', 'trials.view'],
  ['machines', 'machines.view'],
  ['service', 'service.view'],
  ['documents', 'documents.view'],
  ['reports', 'reports.view'],
  ['settings', 'settings.view'],
];

/** Pages within a module that need more than the module's own permission. */
const PAGE_PERMISSIONS: Array<[string, string]> = [
  ['settings/users', 'users.manage'],
  ['settings/audit-log', 'audit.view'],
  // Deleted items exposes what was removed across the whole business, so it
  // follows the same manager-level line the navigation already draws.
  ['settings/deleted-items', 'reports.financial'],
  ['settings/import', 'customers.manage'],
  ['settings/pdf-templates', 'settings.manage'],
  ['settings/qc-checklists', 'qc_templates.manage'],
  // Company details and integration status are readable by anyone who can see
  // settings — they are needed to check an address or whether Xero is
  // connected. Editing is gated inside the page, not at the route.
  ['sales/invoices', 'invoices.view'],
  // Warehouse has no Sales navigation, but the deliveries board links straight
  // to an order so the picker can read the delivery address and line items.
  // Gating this on `orders.view` rather than `sales.view` keeps that route open
  // without opening the pipeline, quotes or invoices alongside it.
  ['sales/orders', 'orders.view'],
  // Same reasoning as sales/orders above — Warehouse reaches this from its
  // own nav entry and needs it open without the rest of Sales.
  ['sales/reservations', 'reservations.view'],
  ['reports/margin', 'reports.financial'],
];

function requiredPermission(pathname: string): string | null {
  const path = pathname.replace(/^\//, '');
  // Most specific first: a page rule beats its module's rule.
  for (const [prefix, permission] of PAGE_PERMISSIONS) {
    if (path === prefix || path.startsWith(`${prefix}/`)) return permission;
  }
  for (const [prefix, permission] of MODULE_PERMISSIONS) {
    if (path === prefix || path.startsWith(`${prefix}/`)) return permission;
  }
  return null;
}

function RequirePermission({ children }: { children: ReactNode }) {
  const { can } = useAuth();
  const { pathname } = useLocation();
  const permission = requiredPermission(pathname);

  if (permission && !can(permission)) return <NoAccess />;
  return <>{children}</>;
}

function NoAccess() {
  return (
    <div className="py-16">
      <h1 className="section-title mb-2">You do not have access to this page</h1>
      <p className="max-w-prose text-body text-ink-secondary">
        Your role does not include this area of the platform. If you need it for
        your work, ask an administrator to review your permissions.
      </p>
      <Link to="/" className="mt-4 inline-block label text-primary-ink hover:underline">
        Back to the dashboard
      </Link>
    </div>
  );
}

export function App() {
  return (
    <Routes>
      <Route path="/sign-in" element={<SignIn />} />

      <Route element={<Shell />}>
        {/* The dashboard is the home screen; /dashboard and / are the same page. */}
        <Route index element={<Dashboard />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route
          path="/*"
          element={
            <Suspense fallback={<Loading />}>
              <RequirePermission>
              <Routes>
                <Route path="products/*" element={<Products />} />
                <Route path="procurement/*" element={<Procurement />} />
                <Route path="warehouse/*" element={<Warehouse />} />
                <Route path="sales/*" element={<Sales />} />
                <Route path="customers/*" element={<Customers />} />
                <Route path="trials/*" element={<Trials />} />
                <Route path="machines/*" element={<Machines />} />
                <Route path="service/*" element={<Service />} />
                <Route path="documents/*" element={<Documents />} />
                <Route path="reports/*" element={<Reports />} />
                <Route path="settings/*" element={<Settings />} />
                <Route path="tasks" element={<TasksPage />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
              </RequirePermission>
            </Suspense>
          }
        />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function NotFound() {
  return (
    <div className="py-16">
      <p className="font-sans text-caption text-ink-tertiary">
        That page does not exist. Use the navigation or search to find what you need.
      </p>
    </div>
  );
}
